<template>
    <meta-data page="shop-bikes-catalog" />
    <InfoBar :text="$translate('components.info-bar.text')" />
    <Header
        logo="/storage/assets/logos/rectangle_blanc.png"
        :banner="banner"
        :soldes="false"
    />
    <Slider input:input @isLoading="handleLoading" />
    <div class="nav">
        <div class="link">
            <h3 class="h3">
                <RouterLink :to="{ name: 'home' }">
                    {{ $translate('public.shop.home.bikes.home') }}
                </RouterLink>
                <i class="fa-solid fa-chevron-right fa-2xs"></i>
                <RouterLink :to="{ name: 'home-shop' }">
                    {{ $translate('public.shop.home.bikes.shop') }}
                </RouterLink>
                <i class="fa-solid fa-chevron-right fa-2xs"></i>
            </h3>
            <h1>
                <RouterLink
                    :to="{ name: 'bikes-catalog-category' }"
                    class="text-decoration-underline"
                >
                    {{ $translate('public.shop.home.bikes.catalog') }}
                </RouterLink>
            </h1>
        </div>
        <div class="search hide-mobile">
            <input
                type="search"
                placeholder="Rechercher..."
                v-model="search"
                id="search-2"
                @change="
                    $router.push({
                        query: { ...route.query, search: search },
                    });
                    getBikes();
                "
            />
            <i
                @click="
                    $router.push({
                        query: { ...route.query, search: search },
                    });
                    getBikes();
                "
                class="fa-solid fa-magnifying-glass fa-xl search-icon"
            ></i>
        </div>
    </div>
    <div class="flex flex-col md:flex-row">
        <Filters
            :sizes="sizes"
            :brands="brands"
            :utilisations="utilisations"
            :frame_types="frame_types"
            :electrics="electrics"
            @isLoading="handleLoading"
        />
        <LoadingCards v-if="isLoading" class="w-full p-6" :count="12" />
        <div
            v-else-if="bikes.length > 0"
            class="grid grid-cols-[repeat(auto-fill,_minmax(300px,_1fr))] gap-6 p-6 w-full"
        >
            <template v-for="bike in bikes" :key="bike.id">
                <BikeCard :bike="bike" />
            </template>
        </div>
        <div v-else>
            <p class="text-center text-gray-500">
                Aucun vélo disponible pour le moment.
            </p>
        </div>
        <div
            class="fixed top-5 right-5 bg-red py-1 px-2 rounded-full text-white hover:cursor-pointer"
            @click="scrollToTop"
            v-if="scrollButton"
        >
            <i class="fa-solid fa-chevron-up"></i>
        </div>
    </div>
    <Footer />
</template>
<script setup>
import { useApiStore } from '@/js/stores/apiStore';
import { onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Filters from './catalog/partials/Filters.vue';
// Partials
import LoadingCards from '@/js/components/LoadingCards.vue';
import Footer from '@/js/components/public/Footer.vue';
import Header from '@/js/components/public/Header.vue';
import InfoBar from '@/js/components/public/InfoBar.vue';
import { useCookieStore } from '@/js/stores/cookiesStore';
import Slider from './catalog/partials/Slider.vue';

import BikeCard from '@/js/views/public/partial/BikeCard.vue';
const route = useRoute();
const $router = useRouter();
const scrollButton = ref(false);
const apiStore = useApiStore();

const banner = ref('/storage/assets/images/banners/shop.jpg');

const search = ref(route.query.search ? route.query.search : '');

const getQueryValue = (query, key, defaultValue = 'all') => {
    const value = query[key];
    return value
        ? value.split(',').map(c => decodeURIComponent(c))
        : [defaultValue];
};

const input = ref({
    utilisation: getQueryValue(route.query, 'utilisation'),
    brand: getQueryValue(route.query, 'brand'),
    size: getQueryValue(route.query, 'size'),
    frame_type: getQueryValue(route.query, 'frame_type'),
    electric: getQueryValue(route.query, 'electric'),
    stock: getQueryValue(route.query, 'stock'),
    engine: getQueryValue(route.query, 'engine'),
    battery: getQueryValue(route.query, 'battery'),
    promotion: getQueryValue(route.query, 'promotion'),
});

const bikes = ref([]);
const isLoading = ref(true);
const sizes = ref([]);
const utilisations = ref([]);
const brands = ref([]);
const frame_types = ref([]);
const electrics = ref([]);
const page = ref(1);

let noMoreBikes = false;

const getBikes = () => {
    page.value = 1;
    apiStore
        .apiGet(
            '/api/bikes-catalog/' +
                input.value.utilisation +
                '/list?formula=B2C&limit=12&page=' +
                page.value,
            {
                ...input.value,
                search: search.value,
            },
        )
        .then(response => {
            noMoreBikes = false;
            bikes.value = response.data.data.filter(bike => bike.display);
            sizes.value = response.filters.sizes;
            brands.value = response.filters.brands;
            frame_types.value = response.filters.frame_types;
            utilisations.value = response.filters.utilisations;
            electrics.value = response.filters.electrics;
            isLoading.value = false;
            getFavorites();
        });
};

getBikes();

let isLoadingMore = false;

const loadMoreBikes = () => {
    if (isLoadingMore || isLoading.value || noMoreBikes) {
        return;
    }

    isLoadingMore = true;
    page.value += 1;

    apiStore
        .apiGet(
            '/api/bikes-catalog/' +
                input.value.utilisation +
                '/list?formula=B2C&limit=12&page=' +
                page.value,
            {
                ...input.value,
                search: search.value,
            },
        )
        .then(response => {
            const newBikes = response.data.data.filter(bike => bike.display);
            if (newBikes.length == 0) {
                noMoreBikes = true;
            } else {
                bikes.value = [...bikes.value, ...newBikes]; // Append new bikes
            }
            isLoadingMore = false;
        })
        .catch(() => {
            isLoadingMore = false;
        });
};

// Detect when the user has scrolled to the bottom
const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    // Check if we've reached the bottom of the page
    if (scrollTop + clientHeight >= scrollHeight - 550) {
        loadMoreBikes();
    }
};

onMounted(() => {
    window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});

const handleLoading = (inputValue, searchValue) => {
    isLoading.value = true;
    input.value = inputValue;
    search.value = searchValue;
    getBikes();
};

const getFavorites = async () => {
    apiStore
        .call('POST', '/api/favorites')
        .then(response => {
            const favoritesMap = new Map(
                response.bikes.map(item => [item.id, item]),
            );
            bikes.value.forEach(accessory => {
                accessory.favorite = favoritesMap.has(accessory.id);
            });
            const nbrFav =
                Object.keys(response.bikes).length +
                Object.keys(response.accessories).length;
            useCookieStore().setFavCount(nbrFav);
        })
        .catch(error => {
            console.log(error);
        });
};

window.onscroll = () => {
    if (
        document.body.scrollTop > 2000 ||
        document.documentElement.scrollTop > 2000
    ) {
        scrollButton.value = true;
    } else {
        scrollButton.value = false;
    }
};

const scrollToTop = () => {
    document.body.scrollTop = 850;
    document.documentElement.scrollTop = 850;
};
</script>
<style lang="scss" scoped>
@use '@/scss/variables' as vars;

.nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 2rem 2rem;
    width: 100%;

    @media screen and (max-width: vars.$mediumBreakpoint) {
        justify-content: flex-start;
        padding: 0 1rem;
    }

    .link {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        h1 {
            font-size: vars.$primaryFontSize;
            font-weight: normal;
            margin: 0;
        }

        h3 {
            font-size: vars.$primaryFontSize;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            margin: 0;
        }

        a {
            color: vars.$blackColor;
        }

        .fa-solid {
            color: vars.$blackColor;
        }
    }

    .search {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid vars.$redColor;
        border-radius: 0.8rem;

        input {
            &::-ms-clear {
                display: none;
            }

            &::-webkit-search-cancel-button {
                -webkit-appearance: none;
                height: 20px;
                width: 20px;
                background: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Cpath d='M10 10 L30 30 M30 10 L10 30' stroke='%23d60b52' stroke-width='4'/%3E%3C/svg%3E");
                background-repeat: no-repeat;
                background-position: center;
                background-size: cover;
                cursor: pointer;
            }

            width: 20rem;
            margin: 0;
            background-color: unset;
            border: none;
        }

        .fa-solid {
            margin-right: 1rem;
            color: vars.$redColor;
            font-size: vars.$primaryFontSize;
        }

        .search-icon {
            cursor: pointer;
        }
    }
}
</style>
